import { io } from 'socket.io-client';
import { getAccessToken, ensureFreshToken, refreshTokens } from '../api/tokenManager';

let socket = null;
let onStatusChange = null;
let recovering = false;

export function setStatusCallback(cb) {
  onStatusChange = cb;
}

export function connect() {
  // Tear down any previous socket, even a disconnected one. Checking `.connected`
  // here would leave an idle socket still retrying in the background with a dead
  // token while a second one is created alongside it.
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }

  socket = io(window.location.origin, {
    // A callback, not an object: socket.io re-invokes this on every connection AND
    // every reconnection attempt, so the handshake always carries the token that is
    // in storage right now rather than the one captured when connect() was called.
    auth: (cb) => cb({ token: getAccessToken() }),
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
    timeout: 20000,
  });

  socket.on('connect', () => {
    recovering = false;
    onStatusChange?.('connected');
  });

  socket.on('disconnect', (reason) => {
    onStatusChange?.('disconnected');
    // The server drops idle/backgrounded tabs with `io server disconnect`, and in that
    // case socket.io will NOT auto-reconnect — we have to ask for it explicitly.
    if (reason === 'io server disconnect') {
      recoverAndReconnect();
    }
  });

  socket.on('connect_error', (err) => {
    const message = err?.message || '';

    // An expired token is recoverable: refresh it, then let socket.io retry. Retrying
    // with the same dead token (the old behaviour) just loops forever.
    if (message.includes('Token expired') || message.includes('Invalid token')) {
      onStatusChange?.('reconnecting');
      recoverAndReconnect();
      return;
    }

    onStatusChange?.('error');
  });

  // Reconnection lives on the Manager (`socket.io`), not the socket. Listening for
  // these on the socket itself is a no-op in socket.io-client v4 — which is why the
  // status banner used to stick on "disconnected" forever.
  socket.io.on('reconnect_attempt', () => {
    onStatusChange?.('reconnecting');
  });

  socket.io.on('reconnect', () => {
    onStatusChange?.('connected');
  });

  socket.io.on('reconnect_error', () => {
    onStatusChange?.('reconnecting');
  });

  return socket;
}

/**
 * Refresh the access token, then reconnect. Guarded so that a storm of failed
 * connection attempts produces exactly one refresh call in flight.
 */
async function recoverAndReconnect() {
  if (recovering || !socket) return;
  recovering = true;

  try {
    await refreshTokens();
  } catch {
    // Refresh failed — if the session is genuinely dead, the next API call's 401
    // handler will redirect to login. Nothing useful to do from here.
  } finally {
    recovering = false;
  }

  // The auth callback re-reads storage, so this picks up the new token automatically.
  if (socket && !socket.connected) {
    socket.connect();
  }
}

/**
 * Called when the tab wakes up (refocus, visibility, network back online).
 *
 * A backgrounded tab gets its timers throttled, misses socket heartbeats, and is
 * dropped by the server; meanwhile the 15-minute access token quietly expires. This
 * makes coming back to the tab a first-class event: get a valid token, then reconnect.
 */
export async function resume() {
  if (!getAccessToken()) return;

  await ensureFreshToken();

  if (socket && !socket.connected) {
    onStatusChange?.('reconnecting');
    socket.connect();
  }
}

export function disconnect() {
  if (socket) {
    socket.removeAllListeners();
    socket.io.off('reconnect_attempt');
    socket.io.off('reconnect');
    socket.io.off('reconnect_error');
    socket.disconnect();
    socket = null;
  }
  recovering = false;
  onStatusChange?.('disconnected');
}

export function getSocket() {
  return socket;
}

export function isConnected() {
  return socket?.connected ?? false;
}

/** Tell the server we've read up to `messageId` in a channel. */
export function markChannelRead(channelId, messageId) {
  if (socket?.connected && channelId && messageId) {
    socket.emit('channel:mark_read', { channelId, messageId });
  }
}

/** Tell the server we've read up to `messageId` in a DM. */
export function markDmRead(dmConversationId, messageId) {
  if (socket?.connected && dmConversationId && messageId) {
    socket.emit('dm:mark_read', { dmConversationId, messageId });
  }
}
